/**
 * Pure utility helpers for the Email page — extracted from `pages/Email.jsx`
 * (Vague 113 Wave 1u3 refactor step 1). No React, no state, no API: keeping
 * these pure makes them easy to unit-test and reuse across mailbox components.
 */
import { Inbox, Star, Send, FileText, AlertCircle, Trash2, Clock } from 'lucide-react';

/** System folders the FE always displays in the left sidebar. */
export const SYSTEM_FOLDERS = [
  { id: 'INBOX', label: 'Reçus', icon: Inbox },
  { id: 'STARRED', label: 'Suivis', icon: Star },
  { id: 'SNOOZED', label: 'Reportés', icon: Clock },
  { id: 'SENT', label: 'Envoyés', icon: Send },
  { id: 'DRAFT', label: 'Brouillons', icon: FileText },
  { id: 'SPAM', label: 'Spam', icon: AlertCircle },
  { id: 'TRASH', label: 'Corbeille', icon: Trash2 },
];

/**
 * Smart timestamp formatter for the thread list.
 *   - Same day  → `14:32`
 *   - Same year → `27 avr.`
 *   - Older     → `27 avr. 25`
 */
export function fmtDate(ms) {
  if (!ms) return '';
  const d = new Date(ms);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const sameYear = d.getFullYear() === now.getFullYear();
  return sameYear
    ? d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
    : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: '2-digit' });
}

/**
 * Extract a human-friendly display name from an RFC 5322 address.
 *   `"John Doe <john@x.com>"`  → `John Doe`
 *   `"john@x.com"`             → `john@x.com`
 */
export function extractName(addr) {
  if (!addr) return '';
  const m = addr.match(/^([^<]+)<.+>$/);
  return (m ? m[1] : addr).trim().replace(/^"|"$/g, '');
}

/**
 * Build a label string for an email account dropdown row.
 * Used by the account selector in the sidebar.
 */
export function formatAccountLabel(a) {
  if (a.label) return `📨 ${a.label}`;
  if (a.provider === 'google') return `🟢 ${a.email || 'Gmail'}`;
  if (a.provider === 'microsoft') return `🔵 ${a.email || 'Outlook'}`;
  if (a.provider === 'shared_imap') return `👥 ${a.email || 'Boîte partagée'}`;
  return `✉️ ${a.email || 'SMTP'}`;
}

/**
 * Encode an account choice for the `X-Mailbox-Account` header understood by
 * `routes/mailbox.py`. Shared mailboxes ride on `shared:{cfg_id}`, OAuth
 * accounts just send the provider name.
 */
export function accountHeaderValue(a) {
  if (!a) return '';
  return a.shared_config_id ? `shared:${a.shared_config_id}` : (a.provider || '');
}

/** Stable composite key for the account dropdown rows. */
export function accountKey(a, idx) {
  return a.provider + ':' + (a.shared_config_id || a.email || idx);
}
